import { Question, QuestionType } from "./Questionnaire";
import { User } from "./User";
import { Address, AddressSchema } from "./geocoding";
import tv, { SchemaType } from "typed-object-validator";
import { MultipleQuestionOptions, MultipleTableQuestionOptions } from ".";

export interface Answer {
    userId: number;
    user?: User;
    questionId: number;
    question?: Question;
    valueJson: MultipleAnswerValue | OpenAnswerValue | NumberAnswerValue | LocationAnswerValue | MultipleTableAnswerValue;
    createdTimeStamp: number;
}

export const MultipleAnswerValueSchema = tv.object({
    selected: tv.array(tv.string()).max(1000),
    other: tv.string().optional(),
});
export type MultipleAnswerValue = SchemaType<typeof MultipleAnswerValueSchema>;

export const OpenAnswerValueSchema = tv.object({
    value: tv.string().max(1000),
});
export type OpenAnswerValue = SchemaType<typeof OpenAnswerValueSchema>;

export const NumberAnswerValueSchema = tv.object({
    value: tv.number(),
});
export type NumberAnswerValue = SchemaType<typeof NumberAnswerValueSchema>;

export const LocationAnswerValueSchema = tv.object({
    address: AddressSchema,
});
export type LocationAnswerValue = SchemaType<typeof LocationAnswerValueSchema>;

export const MultipleTableAnswerValueSchema = tv.object({
    selected: tv.array(tv.array(tv.string())),
});
export type MultipleTableAnswerValue = SchemaType<typeof MultipleTableAnswerValueSchema>;

export const EnsureInputAnswerSchema = tv.object({
    input: tv.string().max(200),
});
export type EnsureInputAnswer = SchemaType<typeof EnsureInputAnswerSchema>;

export function getSchemaForAnswerType(type: QuestionType) {
    switch (type) {
        case "open":
            return OpenAnswerValueSchema;
        case "multiple":
            return MultipleAnswerValueSchema;
        case "multipletable":
            return MultipleTableAnswerValueSchema;
        case "location":
            return LocationAnswerValueSchema;
        case "number":
            return NumberAnswerValueSchema;
        case "ensure":
            return EnsureInputAnswerSchema;
        default:
            console.trace("Unknown question type to validate", type);
    }
}

export function validateAnswer(type: QuestionType, answer: unknown) {
    let schema = getSchemaForAnswerType(type);
    if (!schema) {
        console.warn("no schema for question type", type);
        return false;
    }
    let errors = schema.validate(answer);
    if (errors) {
        console.warn("could not validate answer", errors);
        return false;
    } else {
        return true;
    }
}

export function questionToHeader(question: Question<any>): string[] {
    switch (question.type) {
        case "location":
            return ["PostCode", "StraatNummer", "Straat", "Stad", "Land", "Provincie"].map((e) => `${question.parameter}[${e}]`);
        case "multipletable":
            let options = (question as Question<MultipleTableQuestionOptions>).optionsJson!;
            if (options.coded) {
                return options.rows.map((e) => [`${question.parameter}[${e}]`, `${question.parameter}_code[${e}]`]).flat();
            } else {
                return options.rows.map((e) => `${question.parameter}[${e}]`);
            }
        case "multiple":
            if ((question as Question<MultipleQuestionOptions>).optionsJson?.coded) {
                return [question.parameter!, question.parameter! + "_code"];
            } else {
                return [question.parameter!];
            }
        default:
            return [question.parameter!];
    }
}

export function answerToString(question: Question<any>, answer: any | null): string[] {
    if (!answer || !validateAnswer(question.type, answer)) return [""];
    switch (question.type) {
        case "open": {
            let a: OpenAnswerValue = answer;
            return [a.value];
        }
        case "number": {
            let a: NumberAnswerValue = answer;
            return [String(a.value)];
        }
        case "location": {
            let a: LocationAnswerValue = answer;
            return [
                a.address.postalCode ?? "",
                a.address.streetNumber ?? "",
                a.address.street ?? "",
                a.address.city ?? "",
                a.address.country ?? "",
                a.address.province ?? "",
            ];
        }
        case "multiple": {
            let options = (question as Question<MultipleQuestionOptions>).optionsJson!;
            let a: MultipleAnswerValue = answer;

            let stringColumn;
            if (a.other && a.selected.includes("other")) {
                stringColumn = a.selected
                    .filter((e) => e !== "other")
                    .concat([a.other])
                    .join(";");
            } else {
                stringColumn = a.selected.join(";");
            }

            if (options.coded) {
                let selectedCodes = a.selected.map((e) => options.options.findIndex((b) => (typeof b === "string" ? b === e : b.option === e)) + 1);
                return [stringColumn, selectedCodes.join(";")];
            } else {
                return [stringColumn];
            }
        }
        case "multipletable": {
            let options = (question as Question<MultipleTableQuestionOptions>).optionsJson!;
            let a: MultipleTableAnswerValue = answer;
            if (options.coded) {
                return a.selected
                    .map((rowAnswers, row) => [
                        rowAnswers.join(";"),
                        a.selected[row].map((columnAnswer) => options.columns.indexOf(columnAnswer) + 1).join(";"),
                    ])
                    .flat();
            } else {
                return a.selected.map((e) => e.join(";"));
            }
        }
        case "ensure": {
            let a: EnsureInputAnswer = answer;
            return [a.input];
        }
        default:
            return [""];
    }
}
